import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Eye, EyeOff, Info, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FloatInput({ label, id, type = "text", value, onChange, required, placeholder }) {
  return (
    <div className="relative">
      <label htmlFor={id} className="absolute -top-2.5 left-3 z-10 bg-white px-1 text-xs text-gray-500 leading-none select-none">
        {label}{required && <span className="text-[#ED2E24] ml-0.5">*</span>}
      </label>
      <input
        id={id} type={type} value={value} onChange={onChange} placeholder={placeholder}
        className="w-full border border-gray-300 rounded-xl px-3.5 pt-4 pb-3 text-sm text-gray-900 bg-white outline-none focus:border-gray-500 transition-colors placeholder:text-gray-300"
      />
    </div>
  );
}

function PasswordInput({ label, id, value, onChange, required }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <label htmlFor={id} className="absolute -top-2.5 left-3 z-10 bg-white px-1 text-xs text-gray-500 leading-none select-none">
        {label}{required && <span className="text-[#ED2E24] ml-0.5">*</span>}
      </label>
      <input
        id={id} type={show ? "text" : "password"} value={value} onChange={onChange}
        className="w-full border border-gray-300 rounded-xl px-3.5 pt-4 pb-3 pr-10 text-sm text-gray-900 bg-white outline-none focus:border-gray-500 transition-colors"
      />
      <button type="button" onClick={() => setShow(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
        {show ? <EyeOff size={15} /> : <Eye size={15} />}
      </button>
    </div>
  );
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get("token") || "";

  const [token, setToken] = useState(tokenFromUrl);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(4);

  /* Countdown redirect after success */
  useEffect(() => {
    if (!success) return;
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(interval); navigate("/signin"); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [success, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!token.trim()) { setError("Please enter your reset token."); return; }
    if (!password) { setError("Please enter a new password."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: token.trim(), password });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Password reset failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f2f2f2] flex flex-col" data-testid="reset-password-page">
      <div className="pt-5 pb-2 px-6">
        <button onClick={() => navigate("/signin")} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors" data-testid="back-to-signin-link">
          <ArrowLeft size={16} /> Back to Sign In
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg w-full max-w-[460px] p-8 sm:p-10" data-testid="reset-password-card">
          {success ? (
            <div className="text-center py-4" data-testid="reset-password-success">
              <div className="flex justify-center mb-5">
                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center">
                  <CheckCircle size={48} className="text-green-500" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Password updated!</h2>
              <p className="text-gray-500 text-sm mb-5">Your password has been reset successfully.</p>
              {/* Countdown bar */}
              <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 mb-2">
                <p className="text-[13px] text-gray-400">
                  Redirecting to Sign In in{" "}
                  <span className="font-bold text-gray-600" data-testid="redirect-countdown">{countdown}</span>
                  {countdown === 1 ? " second" : " seconds"}…
                </p>
                <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#ED2E24] rounded-full transition-all duration-1000"
                    style={{ width: `${((4 - countdown) / 4) * 100}%` }}
                  />
                </div>
              </div>
              <button
                onClick={() => navigate("/signin")}
                className="text-sm text-[#ED2E24] hover:underline font-medium"
                data-testid="goto-signin-now-btn"
              >
                Go now →
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900">New password</h1>
                <p className="text-gray-400 text-sm mt-1.5">Enter your reset token and choose a new password</p>
              </div>

              {error && (
                <div className="mb-5 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl flex items-start gap-2" data-testid="reset-password-error">
                  <Info size={15} className="mt-0.5 shrink-0" /><span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate data-testid="reset-password-form">
                <div className="space-y-5">
                  {/* Show token field only if not pre-filled from URL */}
                  {!tokenFromUrl && (
                    <FloatInput
                      label="Reset Token" id="reset-token" required
                      value={token} onChange={(e) => setToken(e.target.value)}
                      placeholder="Paste token from your email"
                    />
                  )}
                  {tokenFromUrl && (
                    <div className="bg-green-50 border border-green-100 text-green-700 text-xs px-4 py-2.5 rounded-xl flex items-center gap-2">
                      <CheckCircle size={13} className="shrink-0" />
                      Reset token received from your email link
                    </div>
                  )}
                  <PasswordInput label="New Password (6+ chars)" id="reset-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
                  <PasswordInput label="Confirm New Password" id="reset-confirm" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                </div>

                <button
                  type="submit" disabled={loading}
                  className="mt-7 w-full bg-[#ED2E24] hover:bg-[#c9231a] disabled:opacity-70 text-white font-semibold py-3.5 rounded-full transition-colors text-sm"
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? "Updating password…" : "Reset Password"}
                </button>
              </form>

              <p className="text-center mt-5 text-sm text-gray-500">
                Need a new link?{" "}
                <button onClick={() => navigate("/forgot-password")} className="text-[#ED2E24] hover:underline font-medium" data-testid="goto-forgot-link">
                  Request again
                </button>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
